import { EmailSharePanel } from '@/components/FormShare/EmailSharePanel'
import { TrackingLinkBuilder } from '@/components/FormShare/TrackingLinkBuilder'
import { QRCode } from '@/components/ui/QRCode'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import { DEFAULT_SHARE_MAX_RESPONSES } from '@/contracts/share'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import { getShareSettings, updateShareSettings } from '@/lib/data-client/share'
import { rebasePublicFormUrl } from '@/lib/public-form-url'
import { getTwoPaneGridTemplate, sharePanelPaneDefaults } from '@/lib/resizable-panes'
import { type SystemFeatureKey, useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import type { Form } from '@/types/form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Braces, Copy, Mail, QrCodeIcon, Share2, SlidersHorizontal } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { CSSProperties, KeyboardEvent } from 'react'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

type ShareSection = 'link' | 'email' | 'qr' | 'tracking' | 'contract'

const shareSections: Array<{
  value: ShareSection
  label: string
  description: string
  icon: LucideIcon
  requiredFeature?: SystemFeatureKey
}> = [
  {
    value: 'link',
    label: '分享連結',
    description: '公開 URL 與開放狀態',
    icon: Share2,
    requiredFeature: 'share.link',
  },
  {
    value: 'email',
    label: '寄送問卷連結',
    description: 'Email 收件人、CC 業務與範本',
    icon: Mail,
    requiredFeature: 'share.email',
  },
  {
    value: 'qr',
    label: 'QR Code',
    description: '前端 QR 圖檔，非資料匯出',
    icon: QrCodeIcon,
    requiredFeature: 'share.qr',
  },
  {
    value: 'tracking',
    label: '追蹤參數',
    description: 'UTM、hidden fields、業務代碼',
    icon: SlidersHorizontal,
    requiredFeature: 'share.tracking',
  },
  {
    value: 'contract',
    label: 'Contract Preview',
    description: 'mock-safe payload preview',
    icon: Braces,
    requiredFeature: 'share.contract',
  },
]

export function SharePanel({ form, trigger }: { form: Form; trigger?: ReactNode }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState('')
  const [activeSection, setActiveSection] = useState<ShareSection>('link')
  const featureVisibility = useSystemFeatureVisibility()
  const {
    width: navWidth,
    collapsed: navCollapsed,
    startResize: startNavResize,
    toggleCollapsed: toggleNavCollapsed,
  } = useResizablePane({
    initialWidth: 284,
    config: sharePanelPaneDefaults.nav,
  })
  const sharePanelGridColumns = getTwoPaneGridTemplate({
    navWidth,
    navCollapsed,
    contentMinWidth: 0,
    navConfig: sharePanelPaneDefaults.nav,
  })

  const { data: settings } = useQuery({
    queryKey: ['share-settings', form.id],
    queryFn: () => getShareSettings(form.id),
    enabled: open,
  })

  const publicUrl = rebasePublicFormUrl(settings?.publicUrl, form.id)
  const visibleShareSections = useMemo(
    () =>
      shareSections.filter(
        (section) => !section.requiredFeature || featureVisibility[section.requiredFeature],
      ),
    [featureVisibility],
  )
  const resolvedActiveSection = visibleShareSections.some(
    (section) => section.value === activeSection,
  )
    ? activeSection
    : (visibleShareSections[0]?.value ?? null)
  const showEmailSection = featureVisibility['share.email']
  const showQrSection = featureVisibility['share.qr']
  const showTrackingSection = featureVisibility['share.tracking']
  const showContractSection = featureVisibility['share.contract']

  const contractPreview = useMemo(
    () => ({
      providerState: 'not_configured',
      mode: 'mock_contract_preview',
      shareSettings: settings,
      publicUrl,
    }),
    [publicUrl, settings],
  )

  async function copy(value: string, key: string) {
    await navigator.clipboard.writeText(value)
    setCopied(key)
    window.setTimeout(() => setCopied(''), 1000)
  }

  async function updateSettings(patch: Parameters<typeof updateShareSettings>[1]) {
    await updateShareSettings(form.id, patch)
    await queryClient.invalidateQueries({ queryKey: ['share-settings', form.id] })
  }

  useEffect(() => {
    if (!visibleShareSections.some((section) => section.value === activeSection)) {
      const nextSection = visibleShareSections[0]?.value
      if (nextSection) {
        setActiveSection(nextSection)
      }
    }
  }, [activeSection, visibleShareSections])

  function handleSectionKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!resolvedActiveSection) return
    const currentIndex = visibleShareSections.findIndex(
      (section) => section.value === resolvedActiveSection,
    )
    if (currentIndex < 0) return
    const keyMap: Partial<Record<string, number>> = {
      ArrowDown: Math.min(currentIndex + 1, visibleShareSections.length - 1),
      ArrowRight: Math.min(currentIndex + 1, visibleShareSections.length - 1),
      ArrowUp: Math.max(currentIndex - 1, 0),
      ArrowLeft: Math.max(currentIndex - 1, 0),
      Home: 0,
      End: visibleShareSections.length - 1,
    }
    const nextIndex = keyMap[event.key]
    if (typeof nextIndex !== 'number') return

    event.preventDefault()
    const nextSection = visibleShareSections[nextIndex].value
    setActiveSection(nextSection)
    window.requestAnimationFrame(() => {
      event.currentTarget
        .querySelector<HTMLButtonElement>(`[data-share-section="${nextSection}"]`)
        ?.focus()
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <Share2 className="size-4" />
            分享
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bottom-[10px] left-[10px] right-[10px] top-[10px] flex max-h-none w-auto max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
          <DialogHeader>
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0">
                <DialogTitle className="text-xl">分享問卷</DialogTitle>
                <DialogDescription className="mt-1">
                  管理公開連結與已啟用的分享工具；目前為 mock-safe contract preview。
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div
          data-testid="share-panel-grid"
          className="grid min-h-0 flex-1 bg-slate-50 transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--share-panel-grid-columns)]"
          style={{ '--share-panel-grid-columns': sharePanelGridColumns } as CSSProperties}
        >
          <div className="relative min-h-0 min-w-0 overflow-hidden">
            <aside
              className={cn(
                'min-h-0 border-b border-slate-200 bg-white transition-opacity duration-150 lg:h-full lg:border-r lg:border-b-0',
                navCollapsed && 'pointer-events-none opacity-0',
              )}
              aria-hidden={navCollapsed}
            >
              <ScrollArea className="h-full" viewportClassName="p-3">
                <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                  <div className="text-sm font-semibold text-indigo-950">目前分享狀態</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge variant={settings?.isEnabled === false ? 'secondary' : 'default'}>
                      {settings?.isEnabled === false ? '未開放' : '已開放'}
                    </Badge>
                    <Badge variant="outline" className="bg-white">
                      mock-safe
                    </Badge>
                  </div>
                </div>
                <nav
                  className="space-y-1"
                  aria-label="分享問卷設定"
                  role="tablist"
                  onKeyDown={handleSectionKeyDown}
                >
                  {visibleShareSections.map((section) => (
                    <ShareNavButton
                      key={section.value}
                      section={section}
                      active={resolvedActiveSection === section.value}
                      onClick={() => setActiveSection(section.value)}
                    />
                  ))}
                  {visibleShareSections.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-500">
                      分享工具目前全部隱藏。請由超級管理員在系統功能顯示設定中開啟需要的分享區塊。
                    </div>
                  ) : null}
                </nav>
              </ScrollArea>
            </aside>
            <PaneResizeHandle
              edge="right"
              collapsed={navCollapsed}
              resizeLabel="拖曳調整分享設定左欄寬度"
              collapseLabel="收合分享設定左欄"
              expandLabel="展開分享設定左欄"
              onResizeStart={startNavResize}
              onToggle={toggleNavCollapsed}
            />
          </div>

          <section className="min-h-0 overflow-hidden">
            <ScrollArea className="h-full" viewportClassName="p-4 lg:p-6">
              <div className="mx-auto max-w-6xl space-y-5">
                {resolvedActiveSection === null ? <ShareFeatureHiddenEmptyState /> : null}

                {resolvedActiveSection === 'link' ? (
                  <PanelFrame
                    sectionId="link"
                    title="分享連結"
                    description="管理公開 URL 與基本開放條件。"
                    badge="link settings"
                  >
                    <div
                      className={cn(
                        'grid gap-5',
                        showQrSection && 'xl:grid-cols-[minmax(0,1fr)_280px]',
                      )}
                    >
                      <div className="space-y-5">
                        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                          <Label htmlFor="share-public-url">公開連結</Label>
                          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                            <Input
                              id="share-public-url"
                              readOnly
                              value={publicUrl}
                              className="font-mono text-sm"
                            />
                            <Button
                              type="button"
                              className="shrink-0"
                              onClick={() => void copy(publicUrl, 'url')}
                            >
                              <Copy className="size-4" />
                              {copied === 'url' ? '已複製！' : '複製'}
                            </Button>
                          </div>
                        </div>

                        <div
                          data-testid="share-link-status-summary"
                          className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3"
                        >
                          <div>
                            <div className="text-xs font-medium text-slate-500">問卷</div>
                            <div className="mt-1 truncate text-sm font-semibold text-slate-950">
                              {form.title}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-slate-500">分享狀態</div>
                            <div className="mt-1 flex flex-wrap gap-2">
                              <Badge
                                variant={settings?.isEnabled === false ? 'secondary' : 'default'}
                              >
                                {settings?.isEnabled === false ? '未開放' : '已開放'}
                              </Badge>
                              <Badge variant="outline">mock-safe</Badge>
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-slate-500">Provider 狀態</div>
                            <div className="mt-1 flex flex-wrap gap-2">
                              <Badge variant="outline">provider not configured</Badge>
                            </div>
                          </div>
                        </div>

                        {showEmailSection ? (
                          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <div className="font-semibold text-slate-950">寄送問卷連結</div>
                                <p className="mt-1 text-sm text-slate-500">
                                  使用 Email 範本寄送問卷 URL，並可 CC 多位業務。
                                </p>
                              </div>
                              <Badge variant="outline">provider not configured</Badge>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              className="mt-4"
                              onClick={() => setActiveSection('email')}
                            >
                              <Mail className="size-4" />
                              設定 Email 寄送
                            </Button>
                          </div>
                        ) : null}

                        <ShareSettingsGrid settings={settings} onUpdate={updateSettings} />
                      </div>

                      {showQrSection ? (
                        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-950">
                            <QrCodeIcon className="size-4 text-indigo-600" />
                            快速 QR 預覽
                          </div>
                          <p className="mt-1 text-sm text-slate-500">
                            QR Code 可在左側選單切到完整下載設定。
                          </p>
                          <div className="mt-4 flex justify-center">
                            <QRCode value={publicUrl} size={196} />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </PanelFrame>
                ) : null}

                {showEmailSection && resolvedActiveSection === 'email' ? (
                  <PanelFrame
                    sectionId="email"
                    title="寄送問卷連結"
                    description="用 Email 將問卷連結寄給指定對象，支援多位 CC 業務與可編輯範本。"
                    badge="email contract"
                  >
                    <EmailSharePanel
                      form={form}
                      publicUrl={publicUrl}
                      onCopy={(value, key) => void copy(value, key)}
                      copiedKey={copied}
                    />
                  </PanelFrame>
                ) : null}

                {showQrSection && resolvedActiveSection === 'qr' ? (
                  <PanelFrame
                    sectionId="qr"
                    title="QR Code"
                    description="用於展場、紙本文宣或現場掃碼填答。"
                    badge="frontend QR asset"
                  >
                    <div className="grid gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
                      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="flex justify-center">
                          <QRCode value={publicUrl} size={240} />
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                        <h3 className="font-semibold text-slate-950">掃碼連結內容</h3>
                        <p className="mt-2 break-all rounded-md bg-slate-50 p-3 font-mono text-sm text-slate-700">
                          {publicUrl}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button type="button" onClick={() => void copy(publicUrl, 'qr-url')}>
                            <Copy className="size-4" />
                            {copied === 'qr-url' ? '已複製！' : '複製 URL'}
                          </Button>
                          <Button variant="outline" type="button" disabled>
                            下載 PNG
                          </Button>
                          <Button variant="outline" type="button" disabled>
                            下載 SVG
                          </Button>
                        </div>
                        <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          QR Code 預覽是前端圖檔能力，非回應資料匯出。正式 QR 資產庫、 SVG
                          下載、短網址統計與追蹤後端尚未接線。
                        </div>
                      </div>
                    </div>
                  </PanelFrame>
                ) : null}

                {showTrackingSection && resolvedActiveSection === 'tracking' ? (
                  <PanelFrame
                    sectionId="tracking"
                    title="追蹤參數"
                    description="建立 UTM、hidden fields、業務代碼與活動代碼的測試連結。"
                    badge="tracking contract"
                  >
                    <TrackingLinkBuilder
                      formId={form.id}
                      defaultSalesCode={settings?.defaultSalesCode ?? 'SALES'}
                      defaultCampaignCode={settings?.defaultCampaignCode ?? 'CAMPAIGN'}
                    />
                  </PanelFrame>
                ) : null}

                {showContractSection && resolvedActiveSection === 'contract' ? (
                  <PanelFrame
                    sectionId="contract"
                    title="Contract Preview"
                    description="查看目前分享、Email 與追蹤設定會送給後端的 mock-safe payload。"
                    badge="mock-safe"
                  >
                    <pre className="max-h-[560px] overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-100">
                      {JSON.stringify(contractPreview, null, 2)}
                    </pre>
                  </PanelFrame>
                ) : null}
              </div>
            </ScrollArea>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ShareFeatureHiddenEmptyState() {
  return (
    <div className="grid min-h-[360px] place-items-center rounded-lg border border-dashed border-slate-200 bg-white p-8 text-center shadow-sm">
      <div className="max-w-md">
        <div className="mx-auto grid size-12 place-items-center rounded-lg bg-slate-100 text-slate-500">
          <Share2 className="size-5" />
        </div>
        <h2 className="mt-4 font-semibold text-slate-950">分享區塊目前未顯示</h2>
        <p className="mt-2 text-sm leading-6 text-slate-500">
          分享連結預設顯示；如果目前看不到，代表超級管理員已在系統功能顯示設定的「問卷分享」中關閉。
        </p>
      </div>
    </div>
  )
}

function ShareNavButton({
  section,
  active,
  onClick,
}: {
  section: (typeof shareSections)[number]
  active: boolean
  onClick: () => void
}) {
  const Icon = section.icon
  return (
    <button
      type="button"
      role="tab"
      id={`share-tab-${section.value}`}
      data-share-section={section.value}
      aria-selected={active}
      aria-controls={`share-panel-${section.value}`}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      className={[
        'flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
        active
          ? 'border-indigo-200 bg-indigo-50 text-indigo-950 shadow-sm'
          : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50',
      ].join(' ')}
    >
      <span
        className={[
          'mt-0.5 grid size-9 shrink-0 place-items-center rounded-md',
          active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500',
        ].join(' ')}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{section.label}</span>
        <span
          className={['mt-1 block text-xs', active ? 'text-indigo-700' : 'text-slate-500'].join(
            ' ',
          )}
        >
          {section.description}
        </span>
      </span>
    </button>
  )
}

function PanelFrame({
  sectionId,
  title,
  description,
  badge,
  children,
}: {
  sectionId: ShareSection
  title: string
  description: string
  badge: string
  children: ReactNode
}) {
  return (
    <div
      id={`share-panel-${sectionId}`}
      role="tabpanel"
      aria-labelledby={`share-tab-${sectionId}`}
      className="space-y-4"
    >
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <Badge variant="outline" className="w-fit bg-slate-50">
          {badge}
        </Badge>
      </div>
      {children}
    </div>
  )
}

function ShareSettingsGrid({
  settings,
  onUpdate,
}: {
  settings: Awaited<ReturnType<typeof getShareSettings>> | undefined
  onUpdate: (patch: Parameters<typeof updateShareSettings>[1]) => Promise<void>
}) {
  const maxResponses = settings?.maxResponses ?? DEFAULT_SHARE_MAX_RESPONSES

  return (
    <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2">
      <ToggleRow
        label="開啟問卷"
        checked={settings?.isEnabled ?? true}
        onChange={(checked) => void onUpdate({ isEnabled: checked })}
      />
      <ToggleRow
        label="允許重複填寫"
        checked={settings?.allowRepeat ?? false}
        onChange={(checked) => void onUpdate({ allowRepeat: checked })}
      />
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="share-close-at">截止日期</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => void onUpdate({ closeAt: null })}
          >
            不限日期
          </Button>
        </div>
        <Input
          id="share-close-at"
          type="datetime-local"
          aria-describedby="share-close-at-help"
          defaultValue={settings?.closeAt ?? ''}
          onBlur={(event) => void onUpdate({ closeAt: event.currentTarget.value || null })}
        />
        <p id="share-close-at-help" className="text-xs text-slate-500">
          {settings?.closeAt
            ? '到達截止時間後，公開連結會停止載入與送出。'
            : '目前為不限日期，問卷不會因日期自動關閉。'}
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="share-max-responses">最大回應數</Label>
        <Input
          id="share-max-responses"
          type="number"
          min={0}
          key={maxResponses}
          defaultValue={maxResponses}
          onBlur={(event) =>
            void onUpdate({
              maxResponses: event.currentTarget.value
                ? Number(event.currentTarget.value)
                : DEFAULT_SHARE_MAX_RESPONSES,
            })
          }
        />
      </div>
    </div>
  )
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
      {label}
      <Switch aria-label={label} checked={checked} onCheckedChange={onChange} />
    </div>
  )
}
